import { useAccountPresentationLocale } from '../../features/account-settings/useAccountPresentationText';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConfirmationDialog } from './ConfirmationDialog';
import { NAVIGATION_PURGE_EVENT } from '../../navigation/sessionStore';
import {
  ConfirmContext,
  type DismissConfirmation,
  type ConfirmOptions,
} from './ConfirmContext';

interface ConfirmState {
  show: boolean;
  message: string;
  title: string;
  token: symbol | null;
  renderKey: number;
  options?: ConfirmOptions;
  pending?: boolean;
  error?: string;
}

interface ActiveConfirmation {
  token: symbol;
  onOk: () => void;
  onCancel?: () => void;
  pending?: boolean;
  options?: ConfirmOptions;
}

function confirmationHandle(
  accepted: boolean,
  dismiss: () => void,
): DismissConfirmation {
  const handle = dismiss as DismissConfirmation;
  Object.defineProperty(handle, 'accepted', { value: accepted });
  return handle;
}

const rejectedConfirmation = confirmationHandle(false, () => undefined);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t: receptionText, language } = useAccountPresentationLocale();

  const [state, setState] = useState<ConfirmState>({
    show: false,
    message: '',
    title: 'Confirm Action',
    token: null,
    renderKey: 0,
  });
  const activeRef = useRef<ActiveConfirmation | null>(null);
  const renderKeyRef = useRef(0);

  const settle = useCallback((
    result: 'ok' | 'cancel',
    expectedToken?: symbol,
    force = false,
  ) => {
    const active = activeRef.current;
    if (!active || (expectedToken !== undefined && active.token !== expectedToken)) return;
    if (active.pending && !force) return;
    activeRef.current = null;
    setState((current) => current.token === active.token
      ? { ...current, show: false, token: null }
      : current);
    const callback = result === 'ok' ? active.onOk : active.onCancel;
    callback?.();
  }, []);

  const confirm = useCallback((
    message: string,
    onOk: () => void,
    title = 'Confirm Action',
    onCancel?: () => void,
    options?: ConfirmOptions,
  ): DismissConfirmation => {
    if (activeRef.current) return rejectedConfirmation;
    const token = Symbol('confirmation');
    renderKeyRef.current += 1;
    activeRef.current = { token, onOk, onCancel, options };
    setState({
      show: true,
      message,
      title,
      token,
      renderKey: renderKeyRef.current,
      options,
    });
    return confirmationHandle(true, () => settle('cancel', token, true));
  }, [settle]);

  const accept = async (token: symbol | null) => {
    const active = activeRef.current;
    if (!active || active.token !== token || active.pending) return;
    if (active.options?.isCurrent && !active.options.isCurrent()) { settle('cancel', active.token); return; }
    if (!active.options?.waitForCompletion) { settle('ok', active.token); return; }
    active.pending = true;
    setState(current => ({ ...current, pending: true, error: undefined }));
    try {
      await active.onOk();
      if (activeRef.current !== active) return;
      activeRef.current = null;
      setState(current => current.token === token ? { ...current, show: false, token: null, pending: false } : current);
    } catch (error) {
      if (activeRef.current !== active) return;
      active.pending = false;
      setState(current => current.token === token ? { ...current, pending: false,
        error: error instanceof Error ? error.message : String(error),
      } : current);
    }
  };

  useEffect(() => {
    const dismissForPurge = () => settle('cancel', undefined, true);
    const dismissForEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) settle('cancel');
    };
    window.addEventListener(NAVIGATION_PURGE_EVENT, dismissForPurge);
    document.addEventListener('keydown', dismissForEscape);
    return () => {
      window.removeEventListener(NAVIGATION_PURGE_EVENT, dismissForPurge);
      document.removeEventListener('keydown', dismissForEscape);
      const active = activeRef.current;
      if (!active) return;
      activeRef.current = null;
      active.onCancel?.();
    };
  }, [settle]);

  return <ConfirmContext.Provider value={{ confirm }}>
    {children}
    <ConfirmationDialog key={state.renderKey} open={state.show} title={receptionText(state.title)}
      message={receptionText(state.message)} language={language} busy={Boolean(state.pending)} error={state.error && receptionText(state.error)}
      variant={state.options?.variant} confirmLabel={state.options?.confirmLabel && receptionText(state.options.confirmLabel)}
      cancelLabel={state.options?.cancelLabel && receptionText(state.options.cancelLabel)}
      onConfirm={() => void accept(state.token)} onCancel={() => settle('cancel', state.token ?? undefined)} />
  </ConfirmContext.Provider>;
};
